import React, { useState, useCallback } from 'react';
import { View, Text, Pressable, StatusBar, Platform } from 'react-native';
import { CameraView, useCameraPermissions } from 'expo-camera';
import { StyleSheet } from 'react-native-unistyles';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import Unicon from '@/components/General/Unicon';
import { addEventRegistrantCode } from '@/data/events';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { Query } from '@/utils/react-query/Queries';

export default function AddRegistrationCodeScreen() {
    const router = useRouter();
    const { eventInstanceID, personID, personName } = useLocalSearchParams();
    const queryClient = useQueryClient();

    const [permission, requestPermission] = useCameraPermissions();
    const [error, setError] = useState(null);

    const { data: loggedInUser } = Query.people.loggedInUser();

    const invalidateCodes = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.registrantCodes(eventInstanceID, personID) });
    }, [queryClient, eventInstanceID, personID]);

    const addCodeMutation = useMutation({
        mutationFn: addEventRegistrantCode,
        onSuccess: () => {
            invalidateCodes();
            Toast.show({ type: 'success', text2: 'Code linked successfully.', position: 'top' });
            router.back();
        },
        onError: (err) => {
            setError(err?.message || 'Failed to link code.');
        },
    });

    const handleBarCodeScanned = useCallback(({ data }) => {
        const trimmed = (data || '').trim();
        if (!trimmed) return;
        setError(null);
        addCodeMutation.mutate({
            eventInstanceID,
            personID,
            code: trimmed,
            createdByPersonID: loggedInUser?.id,
        });
    }, [addCodeMutation, eventInstanceID, personID, loggedInUser?.id]);

    // Web should never reach this screen — redirect back.
    if (Platform.OS === 'web') {
        router.back();
        return null;
    }

    return (
        <View style={styles.container}>
            <StatusBar barStyle="light-content" backgroundColor="black" />

            {permission?.granted ? (
                <CameraView
                    style={styles.camera}
                    facing="back"
                    barcodeScannerSettings={{ barcodeTypes: ['qr'] }}
                    onBarcodeScanned={addCodeMutation.isPending ? undefined : handleBarCodeScanned}
                />
            ) : (
                <View style={styles.permissionContainer}>
                    <Unicon name="camera" size={48} colorVariant="textSecondary" />
                    <Text style={styles.permissionTitle}>Camera Access Required</Text>
                    <Text style={styles.permissionSubtitle}>
                        Allow camera access to scan a badge QR code for {personName || 'this registrant'}.
                    </Text>
                    <Pressable onPress={requestPermission} style={styles.permissionButton}>
                        <Text style={styles.permissionButtonText}>Grant Permission</Text>
                    </Pressable>
                </View>
            )}

            {/* Dark overlay with center cutout */}
            {permission?.granted && (
                <View style={styles.overlay} pointerEvents="none">
                    <View style={styles.darkArea} />
                    <View style={styles.middleRow}>
                        <View style={styles.darkArea} />
                        <View style={styles.scanFrame}>
                            <View style={[styles.corner, styles.cornerTopLeft]} />
                            <View style={[styles.corner, styles.cornerTopRight]} />
                            <View style={[styles.corner, styles.cornerBottomLeft]} />
                            <View style={[styles.corner, styles.cornerBottomRight]} />
                        </View>
                        <View style={styles.darkArea} />
                    </View>
                    <View style={styles.darkArea} />
                </View>
            )}

            {/* Back button */}
            <Pressable onPress={() => router.back()} style={styles.backButton}>
                <Unicon name="chevron-back" size={24} style={{ color: 'white' }} />
            </Pressable>

            {/* Instruction text */}
            {permission?.granted && (
                <Text style={styles.instructionText} pointerEvents="none">
                    Scan a badge QR code to link to {personName || 'registrant'}
                </Text>
            )}

            {error && (
                <View style={styles.errorBar}>
                    <Text style={styles.errorText}>{error}</Text>
                </View>
            )}
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        flex: 1,
        backgroundColor: 'black',
    },
    camera: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        flexDirection: 'column',
    },
    darkArea: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.55)',
    },
    middleRow: {
        flexDirection: 'row',
        height: 280,
    },
    scanFrame: {
        width: 280,
        height: 280,
        position: 'relative',
        backgroundColor: 'transparent',
    },
    corner: {
        position: 'absolute',
        width: 36,
        height: 36,
        borderColor: 'white',
        borderWidth: 3,
    },
    cornerTopLeft: { top: 0, left: 0, borderRightWidth: 0, borderBottomWidth: 0 },
    cornerTopRight: { top: 0, right: 0, borderLeftWidth: 0, borderBottomWidth: 0 },
    cornerBottomLeft: { bottom: 0, left: 0, borderRightWidth: 0, borderTopWidth: 0 },
    cornerBottomRight: { bottom: 0, right: 0, borderLeftWidth: 0, borderTopWidth: 0 },
    instructionText: {
        position: 'absolute',
        bottom: 60,
        left: 0,
        right: 0,
        color: 'rgba(255,255,255,0.7)',
        fontSize: 14,
        fontFamily: fonts.medium,
        textAlign: 'center',
        paddingHorizontal: 24,
    },
    backButton: {
        position: 'absolute',
        top: 60,
        left: 20,
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: 'rgba(0,0,0,0.45)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    permissionContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
        gap: 14,
        backgroundColor: colors.background,
    },
    permissionTitle: {
        fontSize: 20,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    permissionSubtitle: {
        fontSize: 15,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
    },
    permissionButton: {
        backgroundColor: colors.primary,
        borderRadius: 10,
        paddingHorizontal: 20,
        paddingVertical: 12,
    },
    permissionButtonText: {
        color: 'white',
        fontSize: 15,
        fontFamily: fonts.bold,
    },
    errorBar: {
        position: 'absolute',
        bottom: 110,
        left: 20,
        right: 20,
        backgroundColor: colors.error,
        borderRadius: 8,
        padding: 12,
    },
    errorText: {
        color: 'white',
        fontSize: 13,
        fontFamily: fonts.body,
        textAlign: 'center',
    },
}));
